import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  Gift,
  Star,
  Sparkles,
  Zap,
  Heart,
  AlertCircle,
} from "lucide-react";
import { motion } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";
import { useCurrency } from "@/context/CurrencyContext";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { useTopProducts } from "@/hooks/useTopProducts";
import { useShipping } from "@/context/ShippingContext";
import { useAuthContext } from "@/lib/AuthProvider";
import { useUserProfile } from "@/context/UserProfileContext";
import Autoplay from "embla-carousel-autoplay";
import LoadingSpinner from "@/components/LoadingSpinner";
import ProductImage from "./ProductImage";
import MobileHeroCard from "./MobileHeroCard";

const HeroSection = () => {
  const { formatPrice, selectedCurrency } = useCurrency();
  const isMobile = useIsMobile();
  const [isReady, setIsReady] = useState(false);
  const [totalNavHeight, setTotalNavHeight] = useState(112); // Default navbar height
  const plugin = useRef(null);
  const {
    products: topProducts,
    loading,
    error,
  } = useTopProducts({ limitCount: 10 });
  const { getActiveShippingTiers, getAvailableShippingTiers, getAllActiveShippingTiers, isTierAvailableInCountry } = useShipping();
  const { user } = useAuthContext();
  const { userProfile, loading: loadingProfile } = useUserProfile();

  useEffect(() => {
    // Initialize the plugin only after component is mounted
    plugin.current = Autoplay({
      delay: 5000,
      stopOnInteraction: false,
    });
    // Set ready state after initialization
    setIsReady(true);
  }, []);

  // Listen for navigation height updates from NavBar (desktop only)
  useEffect(() => {
    if (isMobile) return; // Skip for mobile

    const handleNavHeightChange = (event: CustomEvent) => {
      setTotalNavHeight(event.detail.height);
    };

    // Listen for height updates from NavBar
    window.addEventListener(
      "navHeightUpdate",
      handleNavHeightChange as EventListener
    );

    return () => {
      window.removeEventListener(
        "navHeightUpdate",
        handleNavHeightChange as EventListener
      );
    };
  }, [isMobile]);

  return (
    <section
      className="relative overflow-hidden"
      style={
        isMobile
          ? { minHeight: "auto" } // Content-based height for mobile
          : { height: `calc(100vh - ${totalNavHeight}px)` } // Dynamic height for desktop
      }
    >
      {/* Enhanced Animated Background */}
      <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-jamaica-green/8 via-white via-50% to-jamaica-yellow/8 z-0">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Large Floating Shapes */}
          <div className="absolute top-20 left-20 w-32 h-32 bg-gradient-to-br from-jamaica-yellow/15 to-jamaica-yellow/5 rounded-full animate-bounce delay-100 blur-sm"></div>
          <div className="absolute top-40 right-32 w-24 h-24 bg-gradient-to-br from-jamaica-green/15 to-jamaica-green/5 rounded-full animate-pulse delay-300 blur-sm"></div>
          <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-br from-jamaica-yellow/20 to-jamaica-yellow/5 rounded-full animate-ping delay-500 blur-sm"></div>
          <div className="absolute bottom-40 right-20 w-36 h-36 bg-gradient-to-br from-jamaica-green/12 to-jamaica-green/3 rounded-full animate-bounce delay-700 blur-md"></div>

          {/* Medium Floating Elements */}
          <div className="absolute top-1/3 left-10 w-16 h-16 bg-jamaica-yellow/10 rounded-2xl animate-pulse delay-200 transform rotate-12"></div>
          <div className="absolute top-1/2 right-16 w-12 h-12 bg-jamaica-green/10 rounded-xl animate-bounce delay-400 transform -rotate-12"></div>
          <div className="absolute bottom-1/3 left-1/3 w-14 h-14 bg-jamaica-yellow/12 rounded-2xl animate-ping delay-600 transform rotate-45"></div>

          {/* Gradient Blob Animations */}
          <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-r from-jamaica-green/4 to-jamaica-yellow/4 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-l from-jamaica-yellow/4 to-jamaica-green/4 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-1500"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-gradient-to-br from-jamaica-green/3 to-jamaica-yellow/3 rounded-full mix-blend-multiply filter blur-2xl animate-pulse delay-2000"></div>
        </div>

        {/* Enhanced Pattern Overlay */}
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(#00742f 0.8px, transparent 0.8px), radial-gradient(#ffc901 0.6px, transparent 0.6px)",
            backgroundSize: "30px 30px, 20px 20px",
            backgroundPosition: "0 0, 15px 15px",
          }}
        ></div>
      </div>

      {/* Mobile Hero Card - appears above carousel on mobile only */}
      <MobileHeroCard />

      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center relative z-10 ${isMobile ? 'py-4' : 'py-16'}`}>
        <div className={isMobile ? "flex flex-col gap-8 items-center w-full" : "grid grid-cols-2 gap-12 items-center w-full"}>
          {/* Left side: Enhanced Content */}
          {!isMobile && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="text-left space-y-8 relative"
            >
            {/* Glass Card Container */}
            <div className="relative backdrop-blur-sm bg-white/30 rounded-3xl p-8 shadow-2xl border border-white/40 group landing-glass-card">
              {/* Animated Border Gradient */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green p-[2px] opacity-0 group-hover:opacity-100 landing-border-gradient">
                <div className="rounded-3xl bg-white/80 w-full h-full"></div>
              </div>

              <div className="relative z-10 space-y-6">
                {/* Enhanced Badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-jamaica-yellow/20 to-jamaica-green/20 border-2 border-jamaica-yellow/30 text-jamaica-black font-accent text-sm mb-2 shadow-lg transform landing-badge"
                >
                  <div className="w-6 h-6 bg-jamaica-yellow rounded-full flex items-center justify-center animate-pulse">
                    <Heart className="w-3 h-3 text-white fill-current" />
                  </div>
                  🇯🇲 JamPack'd from Yaad 🇯🇲
                  <Sparkles className="w-4 h-4 text-jamaica-green animate-pulse" />
                </motion.div>

                {/* Enhanced Headline */}
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight font-display"
                >
                  A{" "}
                  <span className="bg-gradient-to-r from-jamaica-green via-jamaica-green/80 to-jamaica-green bg-clip-text text-transparent">
                    Jamaican
                  </span>{" "}
                  Experience In Every Package
                </motion.h1>

                {/* Enhanced Description */}
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="text-base lg:text-lg text-gray-600 md:pr-10 font-sans leading-relaxed"
                >
                  Authentic care packages filled with premium snacks, spices,
                  and cultural treasures from Jamaica, delivered right to your
                  doorstep with love.
                </motion.p>

                {/* Enhanced Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  className="flex flex-col sm:flex-row gap-4"
                >
                  <Button className="h-14 bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white px-8 py-6 text-base font-accent shadow-lg rounded-xl flex items-center justify-center landing-button">
                    <Link to="/products" className="flex items-center gap-2">
                      <Zap className="h-5 w-5" />
                      Shop Now
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-14 border-2 border-jamaica-yellow bg-white/50 backdrop-blur-sm text-jamaica-black px-8 py-6 text-base font-accent shadow-lg rounded-xl flex items-center justify-center landing-button-outline"
                  >
                    <Link
                      to="/products/best-sellers"
                      className="flex items-center gap-2"
                    >
                      <Star className="h-4 w-4 text-jamaica-yellow fill-current" />
                      Browse Best Sellers
                    </Link>
                  </Button>
                </motion.div>

                {/* Enhanced Feature Points */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="pt-6 grid grid-cols-1 sm:grid-cols-2 gap-6"
                >
                  <div className="flex items-center gap-3 bg-white/40 backdrop-blur-sm p-4 rounded-2xl shadow-lg border border-white/30 landing-feature-point">
                    <div className="bg-gradient-to-br from-jamaica-green to-jamaica-green/80 p-3 rounded-2xl shadow-lg transform rotate-3 landing-feature-icon">
                      <Truck className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <span className="text-sm font-bold text-gray-900">
                        Free Shipping
                      </span>
                      <p className="text-xs text-gray-600">
                        {(() => {
                          // Don't show free shipping details if user has no addresses
                          if (!user || loadingProfile || !userProfile?.addresses?.length) {
                            return "Available on qualifying orders";
                          }
                          
                          // Get user's default address country
                          const defaultAddress = userProfile.addresses.find(addr => addr.isDefault) || userProfile.addresses[0];
                          const userCountry = defaultAddress?.country;
                          
                          // Get all active tiers sorted by Firebase configuration order (sortOrder)
                          const allTiers = getAllActiveShippingTiers();
                          
                          // Find the first tier in configuration order that:
                          // 1. Is available in user's country
                          // 2. Has a free shipping threshold
                          for (const tier of allTiers) {
                            // Check if tier is available in user's country
                            const isAvailableInCountry = isTierAvailableInCountry(tier, userCountry);
                            
                            // Check if tier has free shipping threshold
                            const hasFreeShippingThreshold = tier.freeShippingThreshold && tier.freeShippingThreshold > 0;
                            
                            if (isAvailableInCountry && hasFreeShippingThreshold) {
                              return `On orders over ${formatPrice(tier.freeShippingThreshold!)}`;
                            }
                          }
                          
                          // Fallback if no tier qualifies
                          return "Available on qualifying orders";
                        })()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-white/40 backdrop-blur-sm p-4 rounded-2xl shadow-lg border border-white/30 landing-feature-point">
                    <div className="bg-gradient-to-br from-jamaica-yellow to-jamaica-yellow/80 p-3 rounded-2xl shadow-lg transform -rotate-3 landing-feature-icon-reverse">
                      <Gift className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <span className="text-sm font-bold text-gray-900">
                        100% Authentic
                      </span>
                      <p className="text-xs text-gray-600">Jamaican products</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
          )}

          {/* Right side: Enhanced Product slideshow */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
            className="relative group"
          >
            {/* Glass Container */}
            <div className="relative backdrop-blur-sm bg-white/60 shadow-2xl rounded-3xl border-2 border-white/30 p-2 transform-gpu landing-carousel-container">
              {/* Animated Border Gradient */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green p-[3px] opacity-0 group-hover:opacity-100 landing-border-gradient">
                <div className="rounded-3xl bg-white/70 w-full h-full"></div>
              </div>

              <div className="relative z-10 rounded-2xl overflow-hidden aspect-square">
                {loading ? (
                  <div className="aspect-square rounded-2xl overflow-hidden">
                    <ProductImage
                      src="/lovable-uploads/placeholder.png"
                      alt="Loading products"
                      className="w-full h-full object-cover"
                      aspectRatio="square"
                      showLoadingSpinner={false}
                    />
                  </div>
                ) : error ? (
                  <div className="flex justify-center items-center aspect-square text-gray-500 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl">
                    <div className="text-center space-y-2">
                      <div className="w-12 h-12 bg-red-100 rounded-full mx-auto flex items-center justify-center">
                        <AlertCircle className="w-6 h-6 text-red-500" />
                      </div>
                      <p>Unable to load products</p>
                    </div>
                  </div>
                ) : topProducts.length > 0 ? (
                  isReady &&
                  plugin.current && (
                    <Carousel
                      className="w-full"
                      opts={{ align: "center", loop: true }}
                      plugins={[plugin.current]}
                    >
                      <CarouselContent>
                        {topProducts.map((product) => (
                          <CarouselItem key={product.id}>
                            <Link
                              to={`/product/${product.id}`}
                              className="block relative group/item cursor-pointer overflow-hidden rounded-2xl"
                            >
                              <div className="relative overflow-hidden">
                                <ProductImage
                                  src={product.image}
                                  alt={product.name}
                                  className="w-full object-cover transition-all duration-500 group-hover/item:scale-110 group-hover/item:brightness-110"
                                  aspectRatio="square"
                                />

                                {/* Enhanced Sale Badge */}
                                {product.onSale && (
                                  <div className="absolute top-4 left-4 z-10">
                                    <div className="bg-gradient-to-r from-jamaica-black to-jamaica-black/90 text-jamaica-yellow text-xs font-bold px-4 py-2 rounded-full backdrop-blur-sm shadow-xl border border-jamaica-yellow/30 animate-pulse">
                                      <div className="flex items-center gap-1">
                                        <Star className="w-3 h-3 fill-current" />
                                        ON SALE
                                        <Sparkles className="w-3 h-3" />
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Enhanced Bottom Gradient */}
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6 rounded-b-2xl">
                                  <p className="text-white font-bold text-lg group-hover/item:text-jamaica-yellow transition-colors duration-300">
                                    {product.name}
                                  </p>

                                  {/* Enhanced Price Display */}
                                  {product.onSale &&
                                  product.onSalePrice &&
                                  product.onSalePrice < product.price ? (
                                    <div className="flex items-center gap-3 mt-2">
                                      <span className="text-jamaica-yellow font-bold text-xl bg-black/30 px-2 py-1 rounded-lg backdrop-blur-sm">
                                        {formatPrice(product.onSalePrice)} {selectedCurrency}
                                      </span>
                                      <span className="text-white/70 text-base line-through bg-black/20 px-2 py-1 rounded-lg backdrop-blur-sm">
                                        {formatPrice(product.price)}
                                      </span>
                                    </div>
                                  ) : (
                                    <p className="text-white/90 text-lg font-semibold mt-2 bg-black/30 px-2 py-1 rounded-lg backdrop-blur-sm inline-block">
                                      {formatPrice(product.price)} {selectedCurrency}
                                    </p>
                                  )}
                                </div>

                                {/* Enhanced Hover overlay */}
                                <div className="absolute inset-0 bg-gradient-to-t from-jamaica-green/40 to-transparent opacity-0 group-hover/item:opacity-100 transition-all duration-500 flex items-center justify-center">
                                  <div className="bg-white/90 backdrop-blur-sm text-jamaica-green font-bold px-6 py-3 rounded-2xl shadow-xl border border-white/50 transform translate-y-4 group-hover/item:translate-y-0 transition-transform duration-500">
                                    <div className="flex items-center gap-2">
                                      <Sparkles className="w-4 h-4" />
                                      View Product
                                      <ArrowRight className="w-4 h-4" />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Link>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                    </Carousel>
                  )
                ) : (
                  <div className="flex justify-center items-center aspect-square text-gray-500 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl">
                    <div className="text-center space-y-2">
                      <div className="w-12 h-12 bg-gray-200 rounded-full mx-auto flex items-center justify-center">
                        <Gift className="w-6 h-6 text-gray-400" />
                      </div>
                      <p>No products available</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Enhanced Decorative elements */}
            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-gradient-to-br from-jamaica-yellow/20 to-jamaica-yellow/10 rounded-full blur-2xl animate-pulse delay-500"></div>
            <div className="absolute -top-6 -right-6 w-28 h-28 bg-gradient-to-br from-jamaica-green/20 to-jamaica-green/10 rounded-full blur-2xl animate-pulse delay-700"></div>
            <div className="absolute top-1/2 -left-4 w-16 h-16 bg-jamaica-yellow/15 rounded-2xl blur-lg animate-bounce delay-300 transform rotate-45"></div>
            <div className="absolute bottom-1/4 -right-4 w-20 h-20 bg-jamaica-green/15 rounded-2xl blur-lg animate-pulse delay-900 transform -rotate-12"></div>
          </motion.div>
        </div>
      </div>

      {/* Enhanced Bottom decorative shapes */}
      {!isMobile && (
        <>
          <div className="absolute bottom-0 right-0 w-1/3 h-32 bg-gradient-to-tl from-jamaica-green/10 to-transparent rounded-tl-[3rem] backdrop-blur-sm"></div>
          <div className="absolute bottom-12 left-0 w-1/4 h-24 bg-gradient-to-tr from-jamaica-yellow/10 to-transparent rounded-tr-[3rem] backdrop-blur-sm"></div>
        </>
      )}

      {/* Additional floating elements */}
      <div className="absolute top-1/4 left-8 w-4 h-4 bg-jamaica-yellow rounded-full animate-ping delay-1000"></div>
      <div className="absolute top-3/4 right-12 w-6 h-6 bg-jamaica-green rounded-full animate-bounce delay-1200"></div>
      <div className="absolute bottom-1/3 left-1/4 w-3 h-3 bg-jamaica-yellow rounded-full animate-pulse delay-1400"></div>
    </section>
  );
};

export default HeroSection;
